import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, Box, Typography, TextField, Alert,
  IconButton, Chip, CircularProgress, Slide, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as NextIcon,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useEmpresa } from '../../context/EmpresaContext';
import config from '../../config';
import axios from 'axios';

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const ActualizarEstadoDialog = ({ open, onClose, orden, onUpdateSuccess, estados = [] }) => {
  const [glosa, setGlosa] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const ESTADOS_BLOQUEADOS = [9, 10];
  const estadoActualId = parseInt(orden?.id_estado_servicio);
  const estadoBloqueado = ESTADOS_BLOQUEADOS.includes(estadoActualId);

  // Calcular siguiente estado en el workflow
  const estadoActualIndex = estados.findIndex(e => e.id === estadoActualId);
  const siguienteEstado = estadoActualIndex >= 0 && estadoActualIndex < estados.length - 1
    ? estados[estadoActualIndex + 1]
    : null;
  const estadoActualInfo = estados.find(e => e.id === estadoActualId);

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setGlosa('');
      setSelectedFile(null);
      setPreviewUrl(null);
      setError('');
      setSuccess(false);
    }
  }, [open]);

  // Preview de imagen seleccionada
  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [selectedFile]);

  const handleSubmit = async () => {
    if (!siguienteEstado || estadoBloqueado) return;
    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('estado', siguienteEstado.id);
    formData.append('fecha_real', new Date().toISOString());
    formData.append('glosa', glosa || `Estado actualizado por ${user.nivel <= 2 ? 'admin' : 'supervisor'} desde web`);
    if (selectedFile) formData.append('imagen', selectedFile);

    try {
      await axios.put(
        `${config.API_BASE_URL}/api/ordenes/${orden.id_orden_servicio}/estado`,
        formData,
        { headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setSuccess(true);
      setTimeout(() => {
        onUpdateSuccess && onUpdateSuccess();
        onClose();
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Error al actualizar estado');
    } finally {
      setLoading(false);
    }
  };

  if (!orden) return null;

  return (
    <Dialog
      open={open} onClose={onClose}
      fullScreen={isMobile} maxWidth="xs" fullWidth
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${estadoActualInfo?.color || '#1A237E'}, ${estadoActualInfo?.color || '#1A237E'}CC)`,
        color: '#fff', px: 2.5, py: 2,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
            OS #{orden.id_orden_servicio}
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
            {orden.nombre_cliente} {orden.apellido_cliente || ''} · {orden.nombre_local || ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2.5 }}>

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>Estado actualizado correctamente</Alert>}

          {/* Estado bloqueado */}
          {estadoBloqueado && (
            <Alert severity="warning" icon={<LockIcon />} sx={{ borderRadius: 2 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Estado bloqueado</Typography>
              <Typography sx={{ fontSize: 12 }}>
                Esta orden está en estado <strong>{estadoActualInfo?.nombre || 'final'}</strong> y no permite más cambios.
              </Typography>
            </Alert>
          )}

          {/* Transición visual: Estado actual → Siguiente */}
          {!estadoBloqueado && (
            <>
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 1.5, mb: 3, py: 2,
              }}>
                {/* Estado actual */}
                <Box sx={{ textAlign: 'center' }}>
                  <Chip label={estadoActualInfo?.nombre || 'Actual'} size="small" sx={{
                    bgcolor: estadoActualInfo?.color || '#94A3B8', color: '#fff', fontWeight: 700, fontSize: 12,
                  }} />
                  <Typography sx={{ fontSize: 10, color: '#94A3B8', mt: 0.5 }}>Actual</Typography>
                </Box>

                {/* Flecha */}
                <NextIcon sx={{ fontSize: 24, color: '#10B981' }} />

                {/* Siguiente estado */}
                {siguienteEstado ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Chip label={siguienteEstado.nombre} size="small" sx={{
                      bgcolor: siguienteEstado.color || '#10B981', color: '#fff', fontWeight: 700, fontSize: 12,
                      boxShadow: `0 2px 8px ${siguienteEstado.color || '#10B981'}40`,
                    }} />
                    <Typography sx={{ fontSize: 10, color: '#94A3B8', mt: 0.5 }}>Siguiente</Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <Chip label="Último estado" size="small" sx={{
                      bgcolor: '#94A3B8', color: '#fff', fontWeight: 700, fontSize: 12,
                    }} />
                  </Box>
                )}
              </Box>

              {/* Indicador de quién actualiza */}
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} icon={false}>
                <Typography sx={{ fontSize: 12 }}>
                  Este cambio quedará registrado como actualización desde <strong>{user.nivel <= 2 ? 'administración' : 'supervisión'}</strong>.
                </Typography>
              </Alert>

              {/* Glosa */}
              <TextField
                fullWidth size="small" label="Comentarios / Glosa"
                multiline rows={3} value={glosa}
                onChange={(e) => setGlosa(e.target.value)}
                placeholder="Describe el motivo del cambio de estado..."
                sx={{ mb: 2, '& .MuiInputBase-root': { borderRadius: 2 } }}
              />

              {/* Imagen */}
              <Box sx={{
                border: '2px dashed #CBD5E1', borderRadius: 3, p: 2, textAlign: 'center',
                mb: 2, position: 'relative',
                bgcolor: previewUrl ? 'transparent' : '#F8FAFC',
              }}>
                {previewUrl ? (
                  <Box sx={{ position: 'relative' }}>
                    <img src={previewUrl} alt="Preview" style={{
                      maxWidth: '100%', maxHeight: 150, borderRadius: 8,
                    }} />
                    <IconButton
                      size="small"
                      onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#EF4444', color: '#fff',
                        width: 24, height: 24, '&:hover': { bgcolor: '#DC2626' } }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <input
                      type="file" accept="image/*" id="file-upload"
                      style={{ display: 'none' }}
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                    />
                    <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
                      <ImageIcon sx={{ fontSize: 32, color: '#CBD5E1', mb: 0.5 }} />
                      <Typography sx={{ fontSize: 13, color: '#64748B' }}>
                        Toca para adjuntar evidencia (opcional)
                      </Typography>
                    </label>
                  </>
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      {/* Footer */}
      {!estadoBloqueado && siguienteEstado && (
        <Box sx={{ borderTop: '1px solid #E2E8F0', p: 2, display: 'flex', gap: 1.5 }}>
          <Box onClick={onClose} sx={{
            flex: 1, py: 1.2, borderRadius: 2.5, textAlign: 'center',
            border: '1.5px solid #E2E8F0', cursor: 'pointer',
            fontSize: 14, fontWeight: 600, color: '#64748B',
            '&:hover': { bgcolor: '#F8FAFC' },
          }}>
            Cancelar
          </Box>
          <Box
            onClick={loading || success ? undefined : handleSubmit}
            sx={{
              flex: 1.5, py: 1.2, borderRadius: 2.5, textAlign: 'center',
              background: loading || success ? '#CBD5E1' : `linear-gradient(135deg, ${siguienteEstado.color || '#10B981'}, ${siguienteEstado.color || '#10B981'}CC)`,
              cursor: loading || success ? 'default' : 'pointer',
              fontSize: 14, fontWeight: 700, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
              '&:hover': loading || success ? {} : { opacity: 0.9 },
            }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> :
              success ? <><CheckIcon sx={{ fontSize: 16 }} /> Actualizado</> :
              <><NextIcon sx={{ fontSize: 16 }} /> Avanzar a {siguienteEstado.nombre}</>}
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default ActualizarEstadoDialog;
